// ============================================================================
// GROUPING TOOLBAR - Toolbar flutuante para agrupamento de cards
// ============================================================================
//
// Este componente aparece quando 2+ cards estão selecionados, oferecendo:
// - Botão "Agrupar" para criar sub-processo
// - Botão "Organizar" para auto-layout
// - Preview do número de cards selecionados
// - Atalhos de teclado (Ctrl+G para agrupar)
//
// ============================================================================

import React, { useState, useCallback, useEffect } from 'react';
import { Package, X, Layers, ChevronUp, Keyboard } from 'lucide-react';
import { AutoLayoutMenu } from './AutoLayoutMenu';
import { CardData } from '../types';

interface GroupingToolbarProps {
  /** Número de cards selecionados */
  selectionCount: number;
  /** Indica se pode agrupar (2+ cards) */
  canGroup: boolean;
  /** Callback para agrupar cards */
  onGroup: (name?: string) => void;
  /** Callback para limpar seleção */
  onClearSelection: () => void;
  /** Posição do toolbar (opcional) */
  position?: { x: number; y: number };
  /** Indica se está em modo multi-select */
  isMultiSelectMode: boolean;
  /** Toggle do modo multi-select */
  onToggleMultiSelect: (enabled: boolean) => void;
  /** Cards selecionados (para auto-layout) */
  selectedCards?: CardData[];
  /** Todos os cards (para auto-layout) */
  allCards?: CardData[];
  /** Callback para atualizar cards (para auto-layout) */
  onUpdateCards?: (cards: CardData[]) => void;
  /** Callback quando raias são aplicadas */
  onSwimlanesApplied?: (lanes: import('../utils/autoLayoutEngine').SwimLaneInfo[]) => void;
}

export const GroupingToolbar: React.FC<GroupingToolbarProps> = ({
  selectionCount,
  canGroup,
  onGroup,
  onClearSelection,
  position,
  isMultiSelectMode,
  onToggleMultiSelect,
  selectedCards = [],
  allCards = [],
  onUpdateCards,
  onSwimlanesApplied,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [customName, setCustomName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+G para agrupar
      if (e.ctrlKey && e.key === 'g' && canGroup) {
        e.preventDefault();
        onGroup();
      }
      // Escape para cancelar seleção
      if (e.key === 'Escape' && selectionCount > 0) {
        onClearSelection();
      }
      // Ctrl+Shift para ativar multi-select
      if (e.ctrlKey && e.shiftKey && !e.key.match(/[a-z]/i)) {
        onToggleMultiSelect(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Desativar multi-select ao soltar Ctrl ou Shift
      if ((e.key === 'Control' || e.key === 'Shift') && !e.ctrlKey && !e.shiftKey) {
        // Mantém ativo se houver seleção
        if (selectionCount === 0) {
          onToggleMultiSelect(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [canGroup, selectionCount, onGroup, onClearSelection, onToggleMultiSelect]);

  const handleGroup = useCallback(() => {
    if (showNameInput && customName.trim()) {
      onGroup(customName.trim());
      setCustomName('');
      setShowNameInput(false);
    } else if (!showNameInput) {
      setShowNameInput(true);
    }
  }, [showNameInput, customName, onGroup]);

  const handleQuickGroup = useCallback(() => {
    onGroup();
  }, [onGroup]);

  // Não mostrar se não há seleção e não está em multi-select mode
  if (selectionCount === 0 && !isMultiSelectMode) {
    return null;
  }

  return (
    <div
      className="fixed z-50 flex items-center gap-2 p-2 bg-white/95 backdrop-blur-sm rounded shadow-lg border border-gray-200"
      style={{
        bottom: 50,
        left: '50%',
        transform: 'translateX(-50%)',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Indicador de Modo */}
      {isMultiSelectMode && selectionCount === 0 && (
        <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-md">
          <Keyboard size={14} className="text-indigo-500" />
          <span className="text-xs text-indigo-600 font-medium">
            Modo Seleção Múltipla
          </span>
          <span className="text-xs text-indigo-400">
            (Clique nos cards)
          </span>
        </div>
      )}

      {/* Contador de Seleção */}
      {selectionCount > 0 && (
        <>
          <div className="flex items-center gap-2 px-3 py-1 bg-indigo-100 rounded-md">
            <Layers size={14} className="text-indigo-600" />
            <span className="text-sm font-semibold text-indigo-700">
              {selectionCount} {selectionCount === 1 ? 'card' : 'cards'}
            </span>
          </div>

          {/* Input de Nome (expandido) */}
          {showNameInput && (
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Nome do sub-processo..."
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              style={{ width: 200 }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGroup();
                if (e.key === 'Escape') {
                  setShowNameInput(false);
                  setCustomName('');
                }
              }}
            />
          )}

          {/* Botão Agrupar */}
          {canGroup && (
            <>
              {showNameInput ? (
                <button
                  onClick={handleGroup}
                  disabled={!customName.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Package size={14} />
                  Criar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleQuickGroup}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
                    title="Agrupar com nome automático (Ctrl+G)"
                  >
                    <Package size={14} />
                    Agrupar
                  </button>
                  <button
                    onClick={() => setShowNameInput(true)}
                    className="flex items-center gap-1 px-2 py-1.5 text-indigo-600 text-sm hover:bg-indigo-50 rounded-md transition-colors"
                    title="Definir nome personalizado"
                  >
                    <ChevronUp size={14} />
                  </button>
                </>
              )}
            </>
          )}

          {/* Menu de Auto-Layout */}
          {onUpdateCards && selectedCards.length >= 2 && (
            <AutoLayoutMenu
              selectedCards={selectedCards}
              allCards={allCards}
              onUpdateCards={onUpdateCards}
              onSwimlanesApplied={onSwimlanesApplied}
              selectionCount={selectionCount}
            />
          )}

          {/* Botão Cancelar */}
          <button
            onClick={() => {
              onClearSelection();
              setShowNameInput(false);
              setCustomName('');
            }}
            className="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            title="Cancelar seleção (Esc)"
          >
            <X size={16} />
          </button>
        </>
      )}

      {/* Dica de Atalho */}
      {canGroup && !showNameInput && (
        <div className="text-xs text-gray-400 ml-2 hidden sm:block">
          Ctrl+G
        </div>
      )}
    </div>
  );
};

export default GroupingToolbar;
